import { Pen, Plus, RotateCcw, Trash2, X } from "lucide-react";
import { FC, useMemo, useState } from "react";
import {
  FormattedMessage,
  MessageDescriptor,
  defineMessages,
  useIntl,
} from "react-intl";
import { User, UserStatus } from "~/api/model";
import { useGetCurrentUser, useListUsers } from "~/api/users/users";
import { Loader } from "~/components/Loader";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { UserAvatar } from "~/components/ui/user-avatar";
import { useOrganizationParams } from "~/helpers";
import { useTrackPageVisit } from "~/hooks/useTrackPageVisit";
import { ChangeRoleDialog } from "./team/ChangeRoleDialog";
import { DeleteUserDialog } from "./team/DeleteUserDialog";
import { InviteUserDialog } from "./team/InviteUserDialog";
import { ReactivateUserDialog } from "./team/ReactivateUserDialog";
import { SettingsUserRole } from "./types";

interface TableHeader {
  id: string;
  defaultMessage: string;
  className?: string;
}

const roles: Record<SettingsUserRole, MessageDescriptor> = defineMessages({
  OWNER: {
    id: "settings.team.role.owner",
    defaultMessage: "Admin",
  },
  MEMBER: {
    id: "settings.team.role.member",
    defaultMessage: "Member",
  },
  GUEST: {
    id: "settings.team.role.guest",
    defaultMessage: "Guest",
  },
});

const sortUsers = (a: User, b: User): number => {
  const aDeactivated =
    (a as User & { deactivatedAt?: string | null }).deactivatedAt != null;
  const bDeactivated =
    (b as User & { deactivatedAt?: string | null }).deactivatedAt != null;

  if (aDeactivated && !bDeactivated) {
    return 1;
  }
  if (!aDeactivated && bDeactivated) {
    return -1;
  }
  if (aDeactivated && bDeactivated) {
    return a.name.localeCompare(b.name);
  }

  if (
    a.status === UserStatus.FORCE_CHANGE_PASSWORD &&
    b.status !== UserStatus.FORCE_CHANGE_PASSWORD
  ) {
    return -1;
  }
  if (
    a.status !== UserStatus.FORCE_CHANGE_PASSWORD &&
    b.status === UserStatus.FORCE_CHANGE_PASSWORD
  ) {
    return 1;
  }
  return a.name.localeCompare(b.name);
};

export const SettingsTeam: FC = () => {
  useTrackPageVisit("app/settings_team/visit");
  const [showChangeRoleDialog, setShowChangeRoleDialog] = useState(false);
  const [showInviteUserDialog, setShowInviteUserDialog] = useState(false);
  const [showDeleteUserDialog, setShowDeleteUserDialog] = useState(false);
  const [showReactivateUserDialog, setShowReactivateUserDialog] =
    useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const { organizationId } = useOrganizationParams();
  const { data: users, isLoading } = useListUsers(organizationId);
  const { data: currentUser } = useGetCurrentUser();
  const { formatMessage } = useIntl();

  const sortedUsers = useMemo(() => {
    if (!users) return [];
    return [...users].sort(sortUsers);
  }, [users]);
  const tableHeaders: TableHeader[] = [
    {
      id: "settings.team.table.name",
      defaultMessage: "Name",
      className: "px-4",
    },
    {
      id: "settings.team.table.email",
      defaultMessage: "Email",
      className: "px-4 w-64 whitespace-nowrap",
    },
    {
      id: "settings.team.table.role",
      defaultMessage: "Role",
      className: "px-4 w-32 whitespace-nowrap",
    },
    {
      id: "",
      defaultMessage: "",
      className: "w-24 whitespace-nowrap",
    },
  ];

  const handleEditClick = (user: User) => {
    setSelectedUser(user);
    setShowChangeRoleDialog(true);
  };

  const handleInviteClick = () => {
    setShowInviteUserDialog(true);
  };

  const handleDeleteClick = (user: User) => {
    setSelectedUser(user);
    setShowDeleteUserDialog(true);
  };

  const handleReactivateClick = (user: User) => {
    setSelectedUser(user);
    setShowReactivateUserDialog(true);
  };

  if (isLoading)
    return (
      <div className="flex justify-center items-center w-[924px] h-[50vh]">
        <Loader />
      </div>
    );

  return (
    <div className="flex flex-col gap-6 mt-2 w-full">
      <div className="flex justify-between">
        <div className="flex flex-col gap-2">
          <h3 className="text-2xl font-semibold">
            <FormattedMessage id="settings.team" defaultMessage={"Team"} />
          </h3>
          <span className="text-sm text-muted-foreground">
            <FormattedMessage
              id="settings.team.hint"
              defaultMessage={"Managing the members of your organization"}
            />
          </span>
        </div>
        <Button
          className="bg-blue-600 hover:bg-blue-700 cursor-pointer text-sm px-4"
          size="sm"
          onClick={handleInviteClick}
        >
          <Plus className="w-4 h-4 mr-2" />
          <FormattedMessage id="settings.team.invite" defaultMessage="Invite" />
        </Button>
      </div>
      <Table className="w-full table-fixed">
        <TableHeader>
          <TableRow className="!text-zinc-500 !text-sm !font-medium">
            {tableHeaders.map((header) => (
              <TableHead key={header.id} className={header.className}>
                {header.id && (
                  <FormattedMessage
                    id={header.id}
                    defaultMessage={header.defaultMessage}
                  />
                )}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedUsers.map((user) => {
            const isDeactivated =
              (user as User & { deactivatedAt?: string | null })
                .deactivatedAt != null;
            return (
              <TableRow
                key={user.id}
                className={isDeactivated ? "opacity-50" : ""}
              >
                <TableCell className="font-semibold p-4">
                  {user.status === UserStatus.FORCE_CHANGE_PASSWORD ? (
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-[#E4E4E7] rounded-full" />
                      <span className="text-sm font-medium text-muted-foreground">
                        <FormattedMessage
                          id="settings.team.invitationSent"
                          defaultMessage="Invitation sent"
                        />
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <UserAvatar name={user.name} size="sm" />
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{user.name}</span>
                        {isDeactivated && (
                          <Badge variant="outline" className="text-xs">
                            <FormattedMessage
                              id="settings.team.deactivated"
                              defaultMessage="Deactivated"
                            />
                          </Badge>
                        )}
                      </div>
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground p-4 whitespace-nowrap overflow-hidden text-ellipsis">
                  {user.email}
                </TableCell>
                <TableCell className="text-muted-foreground p-4 whitespace-nowrap">
                  {formatMessage(roles[user.role as SettingsUserRole])}
                </TableCell>
                <TableCell>
                  {user.id !== currentUser?.id &&
                    user.status !== UserStatus.FORCE_CHANGE_PASSWORD && (
                      <div className="flex justify-end">
                        {!user.deactivatedAt && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              handleEditClick(user);
                            }}
                          >
                            <Pen className="h-4 w-4" />
                          </Button>
                        )}
                        {user.deactivatedAt && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              handleReactivateClick(user);
                            }}
                          >
                            <RotateCcw className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            handleDeleteClick(user);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  {user.status === UserStatus.FORCE_CHANGE_PASSWORD && (
                    <div className="flex justify-end">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          handleDeleteClick(user);
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {showChangeRoleDialog && selectedUser && (
        <ChangeRoleDialog
          open={showChangeRoleDialog}
          setOpen={setShowChangeRoleDialog}
          user={selectedUser}
        />
      )}
      {showInviteUserDialog && (
        <InviteUserDialog
          open={showInviteUserDialog}
          setOpen={setShowInviteUserDialog}
        />
      )}
      {showDeleteUserDialog && selectedUser && (
        <DeleteUserDialog
          open={showDeleteUserDialog}
          setOpen={setShowDeleteUserDialog}
          user={selectedUser}
        />
      )}
      {showReactivateUserDialog && selectedUser && (
        <ReactivateUserDialog
          open={showReactivateUserDialog}
          setOpen={setShowReactivateUserDialog}
          user={selectedUser}
        />
      )}
    </div>
  );
};
